import type { EntityAvatarIconProps } from "./EntityAvatar.types";

/*
 * TigerConnect's default entity-avatar glyphs, drawn in `currentColor` so the
 * avatar's styles pick the color. Glyphs that carry their own colors
 * (distribution list, escalation, untagged team) live in ./assets instead.
 */

export function UserGlyph({ className }: EntityAvatarIconProps) {
  return (
    <svg viewBox="0 0 90 90" className={className} aria-hidden>
      <path
        fill="currentColor"
        fillRule="evenodd"
        d="M31 59.25C31 51.383 37.495 45 45.5 45 53.505 45 60 51.383 60 59.25c0 2.627-2.16 4.75-4.833 4.75H35.833C33.161 64 31 61.877 31 59.25zm13.638-19c-4.004 0-7.25-3.19-7.25-7.125S40.634 26 44.638 26s7.25 3.19 7.25 7.125-3.246 7.125-7.25 7.125z"
      />
    </svg>
  );
}

/** A full-bleed square with three people cut out — the background shows through them. */
export function GroupGlyph({ className }: EntityAvatarIconProps) {
  return (
    <svg viewBox="0 0 90 90" className={className} aria-hidden>
      <path
        fill="currentColor"
        fillRule="evenodd"
        clipRule="evenodd"
        d="M0,90V0h90v90H0z M37.463,61.987c0.83,0.01,1.659,0.012,2.488,0.013c0.266,0,0.531,0,0.796,0c0.527,0,1.054-0.002,1.582-0.003c0.865-0.003,1.73-0.006,2.596-0.007c0.025,0,0.049,0,0.074,0s0.049,0,0.073,0c0.866,0.001,1.731,0.004,2.596,0.007C48.196,61.998,48.723,62,49.25,62c0.265,0,0.531,0,0.796,0c0.83,0,1.659-0.003,2.488-0.013c4.086-0.05,5.58-1.727,4.377-5.58c-0.548-1.759-1.89-3.552-3.367-4.685c-2.021-1.549-5.285-2.345-8.546-2.359c-3.261,0.015-6.525,0.811-8.545,2.359c-1.478,1.132-2.82,2.926-3.368,4.685C31.884,60.261,33.377,61.938,37.463,61.987z M27.37,28c-3.488,0.073-6.244,2.931-6.293,6.526c-0.059,4.465,3.148,8.721,6.511,8.639c3.25-0.08,6.224-4.127,6.238-8.494C33.838,30.833,31.023,27.924,27.37,28z M19.347,47.207c-1.379,1.054-2.608,2.772-3.095,4.425c-0.211,0.717-0.409,1.533-0.376,2.286c0.096,2.183,2.268,2.713,4.074,2.815c1.629,0.091,3.261,0.118,4.892,0.136c1.419,0.017,2.87-0.035,4.208-0.56c1.123-0.439,1.371-1.046,1.881-2.07c0.285-0.571,0.613-1.118,0.979-1.641c0.756-1.077,1.671-2.048,2.716-2.849c0.633-0.485,1.358-0.915,2.156-1.288C32.412,44.657,23.633,43.931,19.347,47.207z M44.999,31.043c-3.856,0.016-6.847,3.146-6.8,7.161c0.054,4.603,3.296,8.978,6.72,9.069c0.027,0.001,0.053-0.001,0.08-0.001s0.053,0.002,0.08,0.001c3.424-0.091,6.666-4.466,6.72-9.069C51.846,34.188,48.855,31.059,44.999,31.043z M62.628,28c-3.654-0.076-6.469,2.833-6.456,6.671c0.014,4.367,2.987,8.415,6.238,8.494c3.363,0.082,6.57-4.174,6.511-8.639C68.873,30.931,66.117,28.073,62.628,28z M73.746,51.632c-0.487-1.653-1.716-3.371-3.095-4.425c-4.286-3.276-13.065-2.55-17.436,1.255c0.797,0.373,1.523,0.803,2.156,1.288c1.045,0.8,1.96,1.771,2.716,2.849c0.367,0.522,0.695,1.069,0.979,1.641c0.51,1.024,0.759,1.631,1.881,2.07c1.338,0.524,2.789,0.576,4.208,0.56c1.631-0.019,3.263-0.045,4.892-0.136c1.806-0.102,3.979-0.632,4.075-2.815C74.155,53.165,73.958,52.35,73.746,51.632z"
      />
    </svg>
  );
}

export function ForumGlyph({ className }: EntityAvatarIconProps) {
  return (
    <svg viewBox="0 0 72 67" className={className} aria-hidden>
      <path
        fill="none"
        fillRule="evenodd"
        stroke="currentColor"
        strokeLinejoin="round"
        strokeWidth="3"
        d="M67.984 36.817a4.671 4.671 0 0 1-5.838-.243L36.362 13.908 10.191 36.611a4.654 4.654 0 0 1-6.33-.216 4.573 4.573 0 0 1 .196-6.693l29.69-26.208a3.955 3.955 0 0 1 5.26.015l29.315 26.187a4.582 4.582 0 0 1 .156 6.703l-.435.418h-.06zm-56.61.265l-.01-.053c-.229-1.195 1.044-3.09 2.03-3.927l20.931-18.091a3.137 3.137 0 0 1 4.096-.003l20.927 17.934c1.007.87 2.26 2.734 2.023 3.987l-5.048 22.254a6.379 6.379 0 0 1-6.281 5.164H22.703c-3.057.01-5.692-2.15-6.275-5.105l-5.054-22.16z"
      />
    </svg>
  );
}

export function ActivatedTeamGlyph({ className }: EntityAvatarIconProps) {
  return (
    <svg viewBox="0 0 27 23" className={className} aria-hidden>
      <g fill="currentColor">
        <path d="M15.4 19.7369C15.4 20.7455 11.9526 23.0001 7.69999 23.0001C3.4474 23.0001 0 20.7455 0 19.7369C0 14.6908 3.4474 10.6001 7.69999 10.6001C11.9526 10.6001 15.4 14.6908 15.4 19.7369Z" />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M12.7383 11.1818C15.1272 13.0301 16.7279 16.2166 16.7833 19.6284C19.458 19.2542 21.4426 17.9506 21.4426 17.3151C21.4426 13.5793 18.7026 10.5509 15.3226 10.5509C14.6073 10.5509 13.9207 10.6865 13.2826 10.9358C13.0969 11.0083 12.9153 11.0905 12.7383 11.1818Z"
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M20.0039 10.8316C21.6673 12.1371 22.7787 14.3684 22.814 16.7553C24.7371 16.5083 26.1744 15.571 26.1744 15.1175C26.1744 12.4909 24.2479 10.3616 21.8714 10.3616C21.3685 10.3616 20.8857 10.4569 20.4371 10.6322C20.2888 10.6901 20.1442 10.7568 20.0039 10.8316Z"
        />
        <path d="M11.2019 3.6588C11.2019 6.79491 9.59017 9.93102 7.60195 9.93102C5.61373 9.93102 4.00195 6.79491 4.00195 3.6588C4.00195 1.6381 5.61373 0 7.60195 0C9.59017 0 11.2019 1.6381 11.2019 3.6588Z" />
        <path d="M17.6808 5.27966C17.6808 7.40524 16.5884 9.53083 15.2408 9.53083C13.8932 9.53083 12.8008 7.40524 12.8008 5.27966C12.8008 3.91007 13.8932 2.7998 15.2408 2.7998C16.5884 2.7998 17.6808 3.91007 17.6808 5.27966Z" />
        <path d="M23.768 6.25263C23.768 7.83157 22.9621 9.41052 21.968 9.41052C20.9739 9.41052 20.168 7.83157 20.168 6.25263C20.168 5.23526 20.9739 4.41052 21.968 4.41052C22.9621 4.41052 23.768 5.23526 23.768 6.25263Z" />
      </g>
    </svg>
  );
}

export function PatientCareGlyph({ className }: EntityAvatarIconProps) {
  return (
    <svg viewBox="0 0 42 41" className={className} aria-hidden>
      <g fill="currentColor" fillRule="nonzero">
        <path d="M29.236 27.85a.76.76 0 0 1-.765-.75c0-3.675-3.363-6.638-7.49-6.638-4.127 0-7.452 3-7.452 6.638a.76.76 0 0 1-.765.75.76.76 0 0 1-.764-.75c.038-4.463 4.051-8.1 9.02-8.1 4.967 0 8.98 3.637 8.98 8.1a.76.76 0 0 1-.764.75zM21.5 18a4.493 4.493 0 0 1-4.5-4.5c0-2.495 2.013-4.5 4.5-4.5s4.5 2.005 4.5 4.5-2.013 4.5-4.5 4.5zm0-7.405c-1.618 0-2.96 1.31-2.96 2.905 0 1.595 1.342 2.905 2.96 2.905 1.618 0 2.96-1.31 2.96-2.905 0-1.595-1.342-2.905-2.96-2.905z" />
        <path d="M22.643 28.24a.645.645 0 0 1-.643-.644v-3.953c0-.354.29-.643.643-.643.353 0 .643.29.643.643v3.953c-.032.354-.322.643-.643.643z" />
        <path d="M24.66 26.286h-4.017a.645.645 0 0 1-.643-.643c0-.354.29-.643.643-.643h4.05c.353 0 .643.29.643.643 0 .353-.322.643-.675.643z" />
      </g>
    </svg>
  );
}

export function RoleGlyph({ className }: EntityAvatarIconProps) {
  return (
    <svg viewBox="0 0 24 19" className={className} aria-hidden>
      <path
        fill="currentColor"
        fillRule="evenodd"
        d="M14.295 2.603h7.61c1.035 0 1.882.845 1.88 1.876v12.144a1.884 1.884 0 0 1-1.88 1.877H1.882A1.884 1.884 0 0 1 0 16.623V4.48c0-1.032.847-1.876 1.882-1.876h8.054V2.18a2.18 2.18 0 1 1 4.36 0v.423zm-7.994 7.11c1.002 0 1.814-.776 1.814-1.733 0-.956-.812-1.732-1.814-1.732s-1.814.776-1.814 1.732c0 .957.812 1.733 1.814 1.733zm-3.16 4.858h6.73c.001-2.147-1.505-3.888-3.365-3.888-1.858 0-3.365 1.74-3.365 3.888zm16.755-6.096a.657.657 0 1 0 0-1.313h-6.673a.657.657 0 1 0 0 1.313h6.673zm0 3.077a.657.657 0 1 0 0-1.313h-6.673a.656.656 0 1 0 0 1.313h6.673zm0 3.068a.657.657 0 1 0 0-1.313h-6.673a.656.656 0 1 0 0 1.313h6.673zm-7.78-9.743a.862.862 0 0 0 .862-.862v-1.84a.862.862 0 0 0-1.724 0v1.84c0 .476.386.862.862.862z"
      />
    </svg>
  );
}
